"use client";

import { OptimizedImage } from "@/components/ui/optimized-image";
import { cn } from "@/lib/utils";

/** 사진 장수별 줄 나누기 — 빈칸이 생기지 않게. 6장 이상은 3칸씩. */
export function imageRows(count: number): number[] {
  if (count <= 3) return [count];
  if (count === 4) return [2, 2];
  if (count === 5) return [2, 3];
  return Array.from({ length: Math.ceil(count / 3) }, (_, i) =>
    Math.min(3, count - i * 3),
  );
}

interface ChatImageGroupProps {
  images: { url: string; name: string }[];
  /** 비공개 첨부의 서명 URL. undefined = 아직 서명 전(자리표시자). 생략하면 url 그대로. */
  resolveUrl?: (url: string) => string | undefined;
  onOpen?: (index: number) => void;
}

/**
 * 채팅 사진 묶음 — 말풍선 밖, 폭 230px 둥근 모서리 하나.
 * 1장은 원본 비율(최대 260px), 2·3장 한 줄, 4장 2×2, 5장 2+3.
 */
export function ChatImageGroup({
  images,
  resolveUrl = (url) => url,
  onOpen,
}: ChatImageGroupProps) {
  if (images.length === 0) return null;

  const single = images.length === 1;
  const sizes = imageRows(images.length);
  const rows = sizes.map((size, r) => {
    const start = sizes.slice(0, r).reduce((sum, n) => sum + n, 0);
    return images
      .slice(start, start + size)
      .map((image, i) => ({ image, idx: start + i }));
  });

  return (
    <div className="flex w-[230px] max-w-full flex-col gap-[3px] overflow-hidden rounded-[14px]">
      {rows.map((row, r) => (
        <div
          key={r}
          className={cn(
            "grid gap-[3px]",
            row.length === 2 && "grid-cols-2",
            row.length === 3 && "grid-cols-3",
          )}
        >
          {row.map(({ image, idx }) => {
            const src = resolveUrl(image.url);
            return (
              <button
                key={idx}
                type="button"
                disabled={!src}
                onClick={() => onOpen?.(idx)}
                className="block overflow-hidden bg-gray-100 hover:opacity-90 transition-opacity cursor-pointer disabled:cursor-default disabled:hover:opacity-100"
              >
                {src ? (
                  // 1장: 원본 비율(불러오기 전엔 3:4), 최대 260px. 여러 장: 정사각.
                  <OptimizedImage
                    src={src}
                    alt={image.name}
                    width={230}
                    height={single ? 307 : 230}
                    className={cn(
                      "w-full object-cover",
                      single ? "h-auto max-h-[260px]" : "aspect-square h-full",
                    )}
                    unoptimized
                  />
                ) : (
                  // 서명 전 자리표시자 — 사진과 같은 칸
                  <div
                    aria-hidden="true"
                    className={cn(
                      "w-full animate-pulse",
                      single ? "aspect-[3/4] max-h-[260px]" : "aspect-square",
                    )}
                  />
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
